import { DocumentContent, extractDocumentContent } from "../utils/wordContextExtractor";
import { runPipeline } from "./pipelineService";
import { DocumentFormatter, FormattedDocument } from "./documentFormatter";

export type ChatMessage = {
  id: string;
  role: "system" | "user" | "assistant";
  content: string;
  timestamp: string;
};

export type ChatSession = {
  id: string;
  messages: ChatMessage[];
  documentContext?: Partial<DocumentContent>;
};

export type StoredSession = ChatSession & {
  documentId?: string;
  documentPreview?: string;
  documentTitle?: string;
  currentPage?: number;
};
const sessions = new Map<string, StoredSession>();

function generateDocumentId(): string {
  return `doc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function extractFirstPagePreview(text: string | undefined, maxChars = 2000): string | undefined {
  if (!text) return undefined;
  const pageBreakIndex = text.indexOf("\f");
  if (pageBreakIndex >= 0) return text.slice(0, Math.min(pageBreakIndex, maxChars));
  return text.length > maxChars ? text.slice(0, maxChars) + "...<truncated>" : text;
}

// NEW: Function to check if document is empty or blank
function isDocumentEmpty(documentContext: DocumentContent | undefined): boolean {
  if (!documentContext) return true;
  
  // Check if html content is empty or only whitespace
  if (!documentContext.html || documentContext.html.trim().length === 0) {
    return true;
  }
  
  // Check if html only contains empty tags or whitespace
  const textContent = documentContext.html
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&lt;br&gt;/g, '') // Remove <br> entities
    .trim();
  
  if (textContent.length === 0) {
    return true;
  }
  
  return false;
}

export async function initializeChatSession(): Promise<ChatSession> {
  const documentContext = await extractDocumentContent();
  
  // NEW: Check if document is empty before proceeding
  if (isDocumentEmpty(documentContext)) {
    console.log("chatIntegration: Document is empty or blank - not sending to backend");
    
    // Return a session without calling runPipeline
    const emptySession: StoredSession = {
      id: `sess_${Date.now()}`,
      messages: [
        {
          id: "m0",
          role: "system",
          content: "No document content found.",
          timestamp: new Date().toISOString(),
        },
      ],
      documentContext: undefined,
      documentId: undefined,
      documentPreview: undefined,
      currentPage: 1,
    };
    
    sessions.set(emptySession.id, emptySession);
    return emptySession;
  }
  
  // Document has content, proceed with normal flow
  const query = "initialize_session";
  const documentId = generateDocumentId();
  const firstPagePreview = extractFirstPagePreview(documentContext?.html, 2000);

  console.log("chatIntegration: initializing session - sending to pipeline", {
    query,
    documentId,
    hasDocument: !!documentContext,
    documentTitle: documentContext?.metadata?.title,
    documentPreview: firstPagePreview,
  });

  let resp;
  try {
    resp = await runPipeline(query, undefined, documentId, firstPagePreview, undefined, undefined, undefined, undefined, true);
  } catch (error) {
    console.error("chatIntegration: Pipeline initialization failed, creating offline session", error);
    
    // Create offline session when backend is unavailable
    const offlineSession: StoredSession = {
      id: `sess_${Date.now()}`,
      messages: [
        {
          id: "m0",
          role: "system",
          content: "Backend service unavailable. Working in offline mode.",
          timestamp: new Date().toISOString(),
        },
      ],
      documentContext: {
        metadata: documentContext?.metadata,
        html: firstPagePreview ?? undefined,
      },
      documentId,
      documentPreview: firstPagePreview,
      currentPage: 1,
    };
    
    sessions.set(offlineSession.id, offlineSession);
    return offlineSession;
  }

  if (resp && resp.response) {
    try {
      const parsed = JSON.parse(resp.response) as { sessionId?: string; messages?: ChatMessage[] };
      const reducedContext: Partial<DocumentContent> = {
        metadata: documentContext?.metadata,
        html: firstPagePreview ?? undefined,
      };

      const session: StoredSession = {
        id: parsed.sessionId || `sess_${Date.now()}`,
        messages: parsed.messages || [
          {
            id: "m0",
            role: "system",
            content: "Document context loaded.",
            timestamp: new Date().toISOString(),
          },
        ],
        documentContext: reducedContext,
        documentId,
        documentPreview: firstPagePreview,
        currentPage: 1,
      };

      sessions.set(session.id, session);
      return session;
    } catch {
      const reducedContext: Partial<DocumentContent> = {
        metadata: documentContext?.metadata,
        html: firstPagePreview ?? undefined,
      };

      const session: StoredSession = {
        id: `sess_${Date.now()}`,
        messages: [
          {
            id: "m0",
            role: "system",
            content: resp.response || "Document context loaded.",
            timestamp: new Date().toISOString(),
          },
        ],
        documentContext: reducedContext,
        documentId,
        documentPreview: firstPagePreview,
        currentPage: 1,
      };
      sessions.set(session.id, session);
      return session;
    }
  }

  const fallback: StoredSession = {
    id: `sess_${Date.now()}`,
    messages: [
      {
        id: "m0",
        role: "system",
        content: "Document context loaded.",
        timestamp: new Date().toISOString(),
      },
    ],
    documentContext: {
      metadata: documentContext?.metadata,
      html: firstPagePreview ?? undefined,
    },
    documentId,
    documentPreview: firstPagePreview,
    currentPage: 1,
  };
  sessions.set(fallback.id, fallback);
  return fallback;
}

export async function formatDocument(rawText: string): Promise<string> {
  try {
    const formatted = DocumentFormatter.formatComplianceDocument(rawText);
    return DocumentFormatter.formatToWordDocument(formatted);
  } catch (error) {
    console.error('Document formatting failed:', error);
    return rawText; // Return original if formatting fails
  }
}

export async function sendUserMessage(
  sessionId: string,
  message: string
): Promise<{ assistantMessage: ChatMessage }> {
  const session = sessions.get(sessionId);
  const documentPreview = (session as ChatSession & { documentPreview?: string })?.documentPreview;
  const documentId = (session as ChatSession & { documentId?: string })?.documentId;

  const payloadMessage = {
    sessionId,
    userMessage: message,
    timestamp: new Date().toISOString(),
    hasDocument: !!documentId,
    documentTitle: (session as any)?.documentContext?.metadata?.title,
    documentId,
  };

  console.log("chatIntegration: sending user message to pipeline", {
    sessionId,
    userMessagePreview: message.length > 200 ? message.slice(0, 200) + "...<truncated>" : message,
    hasDocument: !!documentId,
    documentTitle: (session as any)?.documentContext?.metadata?.title,
    documentPreview: documentPreview,
  });

  let resp;
  try {
    resp = await runPipeline(
      JSON.stringify(payloadMessage),
      undefined,
      documentId,
      documentPreview,
      undefined,
      undefined,
      undefined,
      undefined,
      false
    );
  } catch (error) {
    console.error("chatIntegration: Pipeline message failed", error);
    
    const assistantMessage: ChatMessage = {
      id: `m_${Date.now()}`,
      role: "assistant",
      content: "Sorry, the backend service is currently unavailable. Please check that the pipeline service is running on port 8001.",
      timestamp: new Date().toISOString(),
    };
    
    return { assistantMessage };
  }

  const assistantMessage: ChatMessage = {
    id: `m_${Date.now()}`,
    role: "assistant",
    content: resp.response || "Sorry, I could not get a reply from the assistant.",
    timestamp: new Date().toISOString(),
  };

  return { assistantMessage };
}